import { EnergyLabel } from "@omni/components/components/Product/energyLabel/energyLabel";
import { KeyFeatures } from "@omni/components/components/Product/Feature/KeyFeatures/keyFeatures.container";
import { useProductPageContext } from "@omni/components/components/ProductPage/context/productPage.context";
import { ProductPage as BaseProductPage } from "@omni/components/components/ProductPage/productPage";
import { useFeatureToggle } from "@omni/components/context/featureToggles.context";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { cn } from "@omni/utils/styles";

import { ECommerceContainer } from "@/components/Product/ECommerce/eCommerce.container";
import { InfoCards } from "@/components/Product/ProductPage/components/infoCards/infoCards.container";

export const ProductPageComponent = () => {
    const {
        product,
        infoLink,
        tooltips,
        energyLabel,
        features,
        cashbackPagePath,
        contextAwareXfPath,
        paintConfiguratorPath
    } = useProductPageContext();

    const isSticky = useFeatureToggle(
        FeatureToggleKey.FEATURE_STICKY_ECOMMERCE
    );

    const isReviewsEnabled = useFeatureToggle(FeatureToggleKey.FEATURE_REVIEWS);

    return (
        <>
            <BaseProductPage.Title>
                <BaseProductPage.Title.RatingSummary />
            </BaseProductPage.Title>
            <div
                className={cn(
                    "tw:flex tw:flex-col tw:md:grid tw:md:items-start tw:md:grid-rows-[auto]",
                    "tw:md:grid-cols-[repeat(2,minmax(10px,1fr))] tw:md:gap-3.75",
                    "tw:lg:grid-cols-[repeat(3,minmax(10px,1fr))]",
                    "tw:space-y-3.75 tw:md:space-y-0"
                )}
            >
                <div
                    className={cn(
                        "tw:md:row-start-1 tw:md:col-start-1 tw:row-end-2 tw:col-end-2"
                    )}
                >
                    <BaseProductPage.ImageViewer />
                </div>
                <div
                    className={cn(
                        "tw:hidden tw:md:block",
                        "tw:md:row-start-2 tw:md:col-start-1 tw:row-end-3 tw:col-end-2",
                        "tw:lg:row-start-1 tw:lg:col-start-2 tw:lg:row-end-2 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.ProductFacing
                        className={cn("tw:hidden", "tw:lg:flex")}
                    />

                    <BaseProductPage.Promotions
                        className={cn("tw:hidden tw:md:block")}
                    />

                    <div
                        className={cn(
                            "tw:flex tw:items-center tw:justify-between tw:flex-wrap"
                        )}
                    >
                        {product.brand && (
                            <div>
                                <BaseProductPage.BrandLogo />
                            </div>
                        )}
                        <EnergyLabel
                            energyLabel={energyLabel}
                            className={cn("tw:flex tw:w-fit tw:justify-end")}
                        >
                            <EnergyLabel.LabelNested />
                            <EnergyLabel.Fiche />
                        </EnergyLabel>
                    </div>

                    <KeyFeatures
                        className={cn("tw:hidden tw:lg:block")}
                        features={features}
                    />

                    <BaseProductPage.PromoBanner />
                </div>

                <div
                    className={cn(
                        {
                            "tw:sticky tw:top-6": isSticky
                        },
                        "tw:row-start-1 tw:col-start-2 tw:row-end-[span_5] tw:col-end-3",
                        "tw:lg:row-start-1 tw:lg:col-start-3 tw:lg:row-end-[span_5] tw:lg:col-end-4"
                    )}
                >
                    <BaseProductPage.RecoverabilityIndex />

                    <EnergyLabel
                        energyLabel={energyLabel}
                        className={cn(
                            "tw:flex tw:md:hidden tw:w-fit tw:justify-start tw:mb-4"
                        )}
                    >
                        <EnergyLabel.LabelNested />
                        <EnergyLabel.Fiche />
                    </EnergyLabel>

                    <BaseProductPage.Promotions
                        className={cn("tw:block tw:md:hidden")}
                    />

                    <ECommerceContainer
                        product={product}
                        infoLink={infoLink || ""}
                        tooltips={tooltips}
                        cashbackPagePath={cashbackPagePath}
                        contextAwareXfPath={contextAwareXfPath}
                        paintConfiguratorPath={paintConfiguratorPath}
                    />

                    <BaseProductPage.PromoBanner
                        className={cn("tw:block tw:md:hidden")}
                    />

                    <InfoCards className={cn("tw:hidden tw:md:flex")} />
                </div>
                <div
                    className={cn(
                        "tw:md:row-start-3 tw:md:col-start-1 tw:row-end-4 tw:col-end-2",
                        "tw:lg:row-start-4 tw:lg:col-start-1 tw:lg:row-end-5 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.ProductBundles />
                    <BaseProductPage.ProductInfo />
                </div>

                <div
                    className={cn(
                        "tw:md:-mt-3.75",
                        "tw:md:row-start-4 tw:md:col-start-1 tw:md:row-end-5 tw:md:col-end-2",
                        "tw:lg:row-start-2 tw:lg:col-start-1 tw:lg:row-end-3 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.RelatedProductsSlider
                        productSource={ProductSliderSources.Accessory}
                    />

                    <BaseProductPage.RelatedProductsSlider
                        productSource={ProductSliderSources.MainProduct}
                    />
                </div>

                {isReviewsEnabled && (
                    <div
                        className={cn(
                            "tw:md:row-start-5 tw:md:col-start-1 tw:md:row-end-6 tw:md:col-end-3",
                            "tw:lg:row-start-5 tw:lg:col-start-1 tw:lg:row-end-6 tw:lg:col-end-4"
                        )}
                    >
                        <BaseProductPage.Review />
                    </div>
                )}

                <InfoCards
                    className={cn(
                        "tw:flex tw:md:hidden",
                        "tw:md:row-start-2 tw:md:col-start-2 tw:md:row-end-3 tw:md:col-end-3",
                        "tw:lg:row-start-2 tw:lg:col-start-3 tw:lg:row-end-3 tw:lg:col-end-4"
                    )}
                />

                <div
                    className={cn(
                        "tw:md:row-start-6 tw:md:col-start-1 tw:row-end-7 tw:col-end-2",
                        "tw:lg:row-start-3 tw:lg:col-start-1 tw:lg:row-end-4 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.RecommendProductsSlider />
                </div>
            </div>

            <div>
                <BaseProductPage.RecentlyViewedProductSlider />
            </div>
        </>
    );
};
